import { ConstructorSchema } from '@common/mapper/types';
import Morphism from 'morphism';
import { PlatformAppForUserGroupDto } from '@data/_generated-api/platform-api/models/platform-app-for-user-group-dto';
import { PlatformNavigationItemForUserGroupDto } from '@data/_generated-api/platform-api/models/platform-navigation-item-for-user-group-dto';
import { PlatformUser } from '@/app/platform/platform-core/user/models/platform-user';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { UserNavigationItem } from '@/app/platform/platform-core/user/models/user-navigation-item';
import { PlatformUserInfoDto } from '@data/_generated-api/platform-api/models/platform-user-info-dto';
import { PlatformUserGroup } from '@/app/platform/platform-core/user/models/platform-user-group';
import { PlatformAanmeldValidationResult } from '@data/_generated-api/platform-api/models/platform-aanmeld-validation-result';
import { OccupationType } from '@/app/platform/platform-shared/enum/occupation-type';

const userInfoToAuthUserSchema: ConstructorSchema<PlatformUserInfoDto, PlatformUser> = {
  constructor: PlatformUser,
  schema: {
    id: src => src.userAccount.id,
    lastName: src => src.userAccount.lastName,
    firstName: src => src.userAccount.firstName,
    email: src => src.userAccount.email,
    displayName: src => src.userAccount.displayName,
    accountCreationMethod: src => Number(src.userAccount.accountCreatedByMethod),
    hasAcceptedTerms: src => src.userAccount.acceptedTerms,
    isVerified: src => src.userAccount.isVerified,
    badgeId: () => '',
    lastAcceptedTermsOn: src => src.userAccount.lastAcceptedTermsOn,
    createdBy: src => src.userAccount.createdBy,
    createdOn: src => src.userAccount.createdOn,
    lastModifiedOn: src => src.userAccount.lastModifiedOn,
    lastUsedAuthenticationMethod: src => Number(src.userAccount.lastUsedAuthenticationMethod),
    userName: src => src.userAccount.userName,
    verifiedOn: src => src.userAccount.verifiedOn,
    primeTimeId: src => src.userAccount.externalId,
    groups: src => Morphism.map(PlatformUserGroup, src.userAccount.userGroups),
    occupationType: () => OccupationType.Onbekend
  }
};

const aanmeldValidationResultToPlatformUserSchema: ConstructorSchema<
  PlatformAanmeldValidationResult,
  PlatformUser
> = {
  constructor: PlatformUser,
  schema: {
    id: src => src.userAccount.id,
    lastName: src => src.userAccount.lastName,
    firstName: src => src.userAccount.firstName,
    email: src => src.userAccount.email,
    displayName: src => src.userAccount.displayName,
    accountCreationMethod: src => Number(src.userAccount.accountCreatedByMethod),
    hasAcceptedTerms: src => src.userAccount.acceptedTerms,
    isVerified: src => src.userAccount.isVerified,
    badgeId: () => '',
    lastAcceptedTermsOn: src => src.userAccount.lastAcceptedTermsOn,
    createdBy: src => src.userAccount.createdBy,
    createdOn: src => src.userAccount.createdOn,
    lastModifiedOn: src => src.userAccount.lastModifiedOn,
    lastUsedAuthenticationMethod: src => Number(src.userAccount.lastUsedAuthenticationMethod),
    userName: src => src.userAccount.userName,
    verifiedOn: src => src.userAccount.verifiedOn,
    primeTimeId: src => src.userAccount.externalId,
    groups: src => Morphism.map(PlatformUserGroup, src.userAccount.userGroups),
    occupationType: () => OccupationType.Onbekend
  }
};

const appForUserGroupToAuthEgiAppSchema: ConstructorSchema<PlatformAppForUserGroupDto, UserEgiApp> = {
  constructor: UserEgiApp,
  schema: {
    machineName: src => src.machineName ?? '',
    poolName: src => src.poolName ?? '',
    rootNavigationItem: src => Morphism.map(UserNavigationItem, src.rootNavigationItem)
  }
};

const navigationItemForUserGroupToAuthNavigationItem: ConstructorSchema<
  PlatformNavigationItemForUserGroupDto,
  UserNavigationItem
> = {
  constructor: UserNavigationItem,
  schema: {
    children: src => Morphism.map(UserNavigationItem, src.children),
    allowedFeaturesForUser: src => src.featuresForUserGroup.map(f => f.pageFeatureId),
    config: src => (src.configurationJson ? JSON.parse(src.configurationJson) : undefined)
  }
};

export const dtoToModelSchemas: ConstructorSchema<any, any>[] = [
  userInfoToAuthUserSchema,
  appForUserGroupToAuthEgiAppSchema,
  navigationItemForUserGroupToAuthNavigationItem,
  aanmeldValidationResultToPlatformUserSchema
];
